'use client'

import { markdown } from '@codemirror/lang-markdown'
import { unifiedMergeView } from '@codemirror/merge'
import { type Extension, StateEffect } from '@codemirror/state'
import { EditorView } from '@codemirror/view'
import { Bars3BottomLeftIcon } from '@heroicons/react/16/solid'
import CodeMirror from '@uiw/react-codemirror'
import { useTheme } from '@wrksz/themes/client'
import React, { forwardRef, useEffect, useRef, useState } from 'react'
import type { ClientUploadedFileData } from 'uploadthing/types'
import useResizeObserver from 'use-resize-observer'
import { cn } from '../../../../../../shared/cn'
import { Button } from '../../../../../actions/Button'
import { customHighlightStyle, darkTheme, lightTheme } from './_utils/editorThemes'
import { createDragDropHandler, createPasteHandler } from './_utils/fileUploadHandlers'
import { useScrollPastEnd } from './_utils/scrollPastEnd'
import { markdownShortcuts } from './EditorToolbar/_utils/markdownShortcuts'
import { EditorToolbar } from './EditorToolbar/EditorToolbar'

type EditorProps = {
  content: string
  setContent: (value: string) => void
  originalContent: string
  onCreateEditor?: (view: EditorView) => void
  onScroll?: (scrollTop: number) => void
  onMouseEnter?: () => void
  showDiff?: boolean
  onDiffComplete?: () => void
  isEditable?: boolean
  small?: boolean
  onAddAttachment?: (attachment: ClientUploadedFileData<{ result: string }>) => void
  passDownProps?: {
    singleView?: boolean
    onFormat?: () => Promise<void> | void
  }
}

export const Editor = forwardRef<HTMLDivElement, EditorProps>(
  (
    {
      content,
      setContent,
      originalContent,
      onCreateEditor,
      onScroll,
      onMouseEnter,
      showDiff = false,
      onDiffComplete,
      isEditable = true,
      small = false,
      onAddAttachment,
      passDownProps
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [editorView, setEditorView] = useState<EditorView | null>(null)
    const [toolbarOpen, setToolbarOpen] = useState(true)

    useEffect(() => {
      setToolbarOpen(!small)
    }, [small])

    React.useImperativeHandle(ref, () => {
      if (!containerRef.current) {
        throw new Error('Container ref is not attached')
      }

      return containerRef.current
    }, [])

    // diffmode=false when all changes are accepted/rejected
    function attachDiffCompletionListener(editorView: EditorView, onDiffComplete: () => void) {
      const listener = EditorView.updateListener.of(update => {
        const hasChunks = editorView.dom.querySelector('.cm-chunkButtons')
        if (update.docChanged || update.transactions.some(tr => tr.effects.length > 0)) {
          if (hasChunks) return
          onDiffComplete()
        }
      })
      editorView.dispatch({ effects: StateEffect.appendConfig.of(listener) })
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: attachDiffCompletionListener is a stable function defined in render scope
    useEffect(() => {
      if (!editorView || !showDiff || !onDiffComplete) return
      attachDiffCompletionListener(editorView, onDiffComplete)
    }, [editorView, showDiff, onDiffComplete])

    useEffect(() => {
      const container = containerRef.current
      if (!container) return

      const handleScroll = () => {
        if (onScroll) {
          onScroll(container.scrollTop)
        }
      }
      container.addEventListener('scroll', handleScroll, { passive: true })

      return () => {
        container.removeEventListener('scroll', handleScroll)
      }
    }, [onScroll])

    const { resolvedTheme, forcedTheme } = useTheme()
    const isDark = resolvedTheme === 'dark' && forcedTheme !== 'light' // resolved theme is not enough on marketing pages

    const scrollPastEnd = useScrollPastEnd(containerRef)
    const { width: containerWidth = 0 } = useResizeObserver({
      ref: containerRef as React.RefObject<Element>
    })

    return (
      <div className='relative flex h-full flex-col rounded-lg bg-backgroundSecondary'>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: editor container uses mouseEnter for sync scrolling, not interactive content */}
        <div
          ref={containerRef}
          className='custom-scrollbar h-full shrink-0 overflow-auto rounded-lg px-1 py-1'
          onMouseEnter={onMouseEnter}
        >
          <CodeMirror
            value={content}
            height='100%'
            extensions={[
              markdown(),
              isDark ? darkTheme : lightTheme,
              customHighlightStyle,
              EditorView.lineWrapping,
              EditorView.editable.of(isEditable),
              createPasteHandler(onAddAttachment),
              createDragDropHandler(onAddAttachment),
              scrollPastEnd,
              markdownShortcuts,

              ...(showDiff
                ? ([
                    unifiedMergeView({
                      original: originalContent
                    }) as Extension
                  ] as Extension[])
                : [])
            ]}
            onChange={setContent}
            onCreateEditor={view => {
              setEditorView(view)
              if (onCreateEditor) onCreateEditor(view)
            }}
            basicSetup={{
              lineNumbers: true,
              foldGutter: false
            }}
          />
        </div>

        {editorView && (
          <div className={cn('absolute inset-x-0.5', small ? 'bottom-[-40]' : 'bottom-2')}>
            {small && !toolbarOpen ? (
              <div className='flex justify-end pr-1'>
                <Button outline size='xs' onClick={() => setToolbarOpen(true)}>
                  <Bars3BottomLeftIcon className='h-5 w-5' />
                </Button>
              </div>
            ) : (
              <EditorToolbar
                editorView={editorView}
                isEditable={isEditable}
                editorWidth={containerWidth}
                {...passDownProps}
                small={small}
                onClose={small ? () => setToolbarOpen(false) : undefined}
              />
            )}
          </div>
        )}
      </div>
    )
  }
)
Editor.displayName = 'Editor'
